import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, IndianRupee, FileText, Receipt, BarChart3, AlertCircle, Download, ChevronLeft, ChevronRight, Filter, FilterX } from 'lucide-react';
import { formatCurrency } from './utils';
import { exportToCSV } from '../../utils/csvExport';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getTodayString } from '../../utils/dateUtils';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 10;

function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
      <p className="text-xs text-slate-500">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm"
          onClick={() => onPage(page - 1)}
          disabled={page === 1}
          className="h-8 w-8 p-0 rounded-lg"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
          .reduce((acc, p, idx, arr) => {
            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
            acc.push(p);
            return acc;
          }, [])
          .map((item, idx) =>
            item === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-1 text-slate-400 text-xs">…</span>
            ) : (
              <Button
                key={item}
                variant={item === page ? 'default' : 'outline'}
                size="sm"
                onClick={() => onPage(item)}
                className={`h-8 w-8 p-0 rounded-lg text-xs ${item === page ? 'bg-slate-900 text-white' : ''}`}
              >
                {item}
              </Button>
            )
          )}
        <Button
          variant="outline" size="sm"
          onClick={() => onPage(page + 1)}
          disabled={page === totalPages}
          className="h-8 w-8 p-0 rounded-lg"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export function SilicaUsageDashboard() {
  const [loading, setLoading] = useState(false);
  const [silicaPurchases, setSilicaPurchases] = useState([]);
  const [silicaLoads, setSilicaLoads] = useState([]);
  const [inventoryData, setInventoryData] = useState({ remainingTons: 0, remainingBalance: 0, remainingBalanceWithGst: 0, breakdown: [] });

  // Pagination states
  const [inventoryPage, setInventoryPage] = useState(1);
  const [purchasePage, setPurchasePage] = useState(1);
  const [loadsPage, setLoadsPage] = useState(1);

  // Silica Loads filter states
  const [loadsClientFilter, setLoadsClientFilter] = useState('all');
  const [loadsStatusFilter, setLoadsStatusFilter] = useState('all');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all expenses with large page size to get all silica purchases
      const expensesRes = await axios.get(`${API}/expenses?page=1&page_size=5000`, { headers });
      const allExpenses = expensesRes.data.items || expensesRes.data || [];
      const silicaExpenses = allExpenses
        .filter(e => e.category === 'Silica Purchase')
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const loadsRes = await axios.get(`${API}/silica-loads?page=1&page_size=1000`, { headers });
      const loadsArray = loadsRes.data.items || loadsRes.data || [];
      const loads = loadsArray.sort((a, b) => new Date(a.date) - new Date(b.date));
      // Use backend-calculated aggregate total_tons so this page matches the
      // Silica Loads page total-bar. The backend applies the full fallback
      // chain (total_kgs_updated → total_tons_updated → kgs → tons →
      // summary_net_tons → total_tons) AND the SVE sign convention
      // (Addition +, Deduction −, Addition/Deduction summary ignored).
      // Recomputing tons client-side from raw items misses all of that.
      const aggregateTotalTons = parseFloat(loadsRes.data.total_tons) || 0;

      setSilicaPurchases(silicaExpenses);
      setSilicaLoads(loads);
      calculateFIFOInventory(silicaExpenses, loads, aggregateTotalTons);
    } catch (error) {
      toast.error('Failed to load silica usage data');
    } finally {
      setLoading(false);
    }
  };

  const calculateFIFOInventory = (purchases, loads, totalTonsSent) => {
    let batches = purchases.map(p => {
      const tons = parseFloat(p.number_of_tons) || 0;
      const pricePerTon = parseFloat(p.amount_per_ton) || 0;
      const gstPct = parseFloat(p.gst_percentage) || 0;
      const pricePerTonWithGst = parseFloat(p.amount_per_ton_with_gst) || (pricePerTon * (1 + gstPct / 100));
      return {
        id: p.id,
        date: p.date,
        originalTons: tons,
        remainingTons: tons,
        pricePerTon,
        gstPct,
        pricePerTonWithGst,
        totalAmount: parseFloat(p.amount) || 0,
      };
    });

    let remainingToDeduct = totalTonsSent;
    for (let batch of batches) {
      if (remainingToDeduct <= 0) break;
      const deduction = Math.min(batch.remainingTons, remainingToDeduct);
      batch.remainingTons -= deduction;
      remainingToDeduct -= deduction;
    }

    const remainingTons = batches.reduce((sum, b) => sum + b.remainingTons, 0);
    const remainingBalance = batches.reduce((sum, b) => sum + b.remainingTons * b.pricePerTon, 0);
    const remainingBalanceWithGst = batches.reduce((sum, b) => sum + b.remainingTons * b.pricePerTonWithGst, 0);

    const breakdown = batches.map(b => ({
      ...b,
      value: b.remainingTons * b.pricePerTon,
      valueWithGst: b.remainingTons * b.pricePerTonWithGst,
    }));

    setInventoryData({
      remainingTons,
      remainingBalance,
      remainingBalanceWithGst,
      breakdown,
      totalPurchased: batches.reduce((sum, b) => sum + b.originalTons, 0),
      totalSent: totalTonsSent,
    });
  };

  const handleExportCSV = () => {
    if (inventoryData.breakdown.length === 0 && silicaPurchases.length === 0) {
      toast.error('No data to export');
      return;
    }
    const inventoryColumns = [
      { key: 'date', label: 'Purchase Date' },
      { key: 'originalTons', label: 'Original Tons' },
      { key: 'remainingTons', label: 'Remaining Tons' },
      { key: 'pricePerTon', label: 'Price Per Ton (₹)' },
      { key: 'gstPct', label: 'GST %' },
      { key: 'pricePerTonWithGst', label: 'Price/Ton incl. GST (₹)' },
      { key: 'value', label: 'Remaining Value (₹)' },
      { key: 'valueWithGst', label: 'Remaining Value incl. GST (₹)' },
    ];
    exportToCSV(inventoryData.breakdown, inventoryColumns, `silica_inventory_${getTodayString()}`);
    toast.success('Silica inventory exported to CSV');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  // ── Paginated slices ──────────────────────────────────────────────────────
  const invBreakdown = inventoryData.breakdown || [];
  const invTotalPages = Math.max(1, Math.ceil(invBreakdown.length / PAGE_SIZE));
  const invPage = Math.min(inventoryPage, invTotalPages);
  const invRows = invBreakdown.slice((invPage - 1) * PAGE_SIZE, invPage * PAGE_SIZE);

  const purchTotalPages = Math.max(1, Math.ceil(silicaPurchases.length / PAGE_SIZE));
  const purchPage = Math.min(purchasePage, purchTotalPages);
  const purchRows = silicaPurchases.slice((purchPage - 1) * PAGE_SIZE, purchPage * PAGE_SIZE);

  const filteredLoads = silicaLoads.filter(load => {
    if (loadsClientFilter !== 'all' && load.client_name !== loadsClientFilter) return false;
    if (loadsStatusFilter !== 'all' && (load.acceptance_status || 'pending') !== loadsStatusFilter) return false;
    return true;
  });
  const loadsClientOptions = [...new Set(silicaLoads.map(l => l.client_name).filter(Boolean))];
  const loadsTotalPages = Math.max(1, Math.ceil(filteredLoads.length / PAGE_SIZE));
  const loadsPageClamped = Math.min(loadsPage, loadsTotalPages);
  const loadsRows = filteredLoads.slice((loadsPageClamped - 1) * PAGE_SIZE, loadsPageClamped * PAGE_SIZE);
  const hasLoadsFilters = loadsClientFilter !== 'all' || loadsStatusFilter !== 'all';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Silica Usage Dashboard</h3>
          <p className="text-sm text-slate-500">Inventory tracking using FIFO method</p>
        </div>
        <Button
          data-testid="export-silica-inventory-csv"
          variant="outline"
          onClick={handleExportCSV}
          disabled={invBreakdown.length === 0}
          className="rounded-xl h-9 w-9 p-0"
          title="Export CSV"
        >
          <Download className="w-4 h-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-5 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg"><TrendingUp className="w-5 h-5 text-white" /></div>
            <span className="text-sm text-white/80">Remaining Tons</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {inventoryData.remainingTons.toLocaleString('en-IN', { maximumFractionDigits: 2 })} tons
          </p>
        </Card>
        <Card className="p-5 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-blue-500 to-blue-600">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg"><IndianRupee className="w-5 h-5 text-white" /></div>
            <span className="text-sm text-white/80">Remaining Value (incl. GST)</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {formatCurrency(inventoryData.remainingBalanceWithGst || 0)}
          </p>
        </Card>
        <Card className="p-5 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-amber-500 to-amber-600">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg"><FileText className="w-5 h-5 text-white" /></div>
            <span className="text-sm text-white/80">Total Purchased</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {(inventoryData.totalPurchased || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} tons
          </p>
        </Card>
        <Card className="p-5 border-0 rounded-2xl shadow-lg bg-gradient-to-br from-purple-500 to-purple-600">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-white/20 rounded-lg"><TrendingDown className="w-5 h-5 text-white" /></div>
            <span className="text-sm text-white/80">Total Sent (Loads)</span>
          </div>
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {(inventoryData.totalSent || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })} tons
          </p>
        </Card>
      </div>

      {/* ── 1. Inventory Breakdown ───────────────────────────────────────── */}
      {invBreakdown.length > 0 && (
        <Card className="p-6 border-0 rounded-2xl shadow-lg">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-600" />
            Inventory Breakdown (FIFO)
            <span className="ml-auto text-xs font-normal text-slate-400">{invBreakdown.length} batches</span>
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 uppercase">Purchase Date</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Orig. Tons</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Rem. Tons</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Price/Ton</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">GST %</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Price/Ton (GST)</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Rem. Value</th>
                  <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Rem. Value (GST)</th>
                </tr>
              </thead>
              <tbody>
                {invRows.map((batch, idx) => (
                  <tr key={batch.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-3 text-slate-900">{new Date(batch.date).toLocaleDateString('en-IN')}</td>
                    <td className="px-3 py-3 text-right text-slate-600">{batch.originalTons.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-3 text-right font-medium text-emerald-600">{batch.remainingTons.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                    <td className="px-3 py-3 text-right text-slate-600">{formatCurrency(batch.pricePerTon)}</td>
                    <td className="px-3 py-3 text-right text-slate-600">{batch.gstPct}%</td>
                    <td className="px-3 py-3 text-right text-slate-600">{formatCurrency(batch.pricePerTonWithGst)}</td>
                    <td className="px-3 py-3 text-right font-medium text-blue-600">{formatCurrency(batch.value)}</td>
                    <td className="px-3 py-3 text-right font-bold text-emerald-700">{formatCurrency(batch.valueWithGst)}</td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                  <td className="px-3 py-3 text-slate-900">TOTAL</td>
                  <td className="px-3 py-3 text-right text-slate-600">{(inventoryData.totalPurchased || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-3 text-right text-emerald-600">{inventoryData.remainingTons.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                  <td className="px-3 py-3 text-right text-slate-500">—</td>
                  <td className="px-3 py-3 text-right text-slate-500">—</td>
                  <td className="px-3 py-3 text-right text-slate-500">—</td>
                  <td className="px-3 py-3 text-right text-blue-600">{formatCurrency(inventoryData.remainingBalance || 0)}</td>
                  <td className="px-3 py-3 text-right text-emerald-700">{formatCurrency(inventoryData.remainingBalanceWithGst || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <Pagination page={invPage} totalPages={invTotalPages} onPage={setInventoryPage} />
        </Card>
      )}

      {/* ── 2. Purchase History ──────────────────────────────────────────── */}
      <Card className="p-6 border-0 rounded-2xl shadow-lg">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
          <Receipt className="w-5 h-5 text-amber-600" />
          Purchase History
          <span className="ml-auto text-xs font-normal text-slate-400">{silicaPurchases.length} purchases</span>
        </h3>
        {silicaPurchases.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Tons</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Price/Ton</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Offline</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Online</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">GST %</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Total (incl. GST)</th>
                  </tr>
                </thead>
                <tbody>
                  {purchRows.map((p, idx) => {
                    const tons = parseFloat(p.number_of_tons) || 0;
                    const pricePerTon = parseFloat(p.amount_per_ton) || 0;
                    const offlineAmt = parseFloat(p.offline_amount) || 0;
                    const onlineAmt = parseFloat(p.online_amount) || 0;
                    const gstPct = parseFloat(p.gst_percentage) || 0;
                    const totalWithGst = parseFloat(p.amount) || 0;

                    return (
                      <tr key={p.id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-3 text-slate-900">{new Date(p.date).toLocaleDateString('en-IN')}</td>
                        <td className="px-3 py-3 text-right text-slate-600">{tons > 0 ? tons.toLocaleString('en-IN') : '—'}</td>
                        <td className="px-3 py-3 text-right text-slate-600">{pricePerTon > 0 ? formatCurrency(pricePerTon) : '—'}</td>
                        <td className="px-3 py-3 text-right text-amber-600">{offlineAmt > 0 ? formatCurrency(offlineAmt) : '—'}</td>
                        <td className="px-3 py-3 text-right text-blue-600">{onlineAmt > 0 ? formatCurrency(onlineAmt) : '—'}</td>
                        <td className="px-3 py-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${gstPct > 0 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                            {gstPct}%
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-slate-900">{totalWithGst > 0 ? formatCurrency(totalWithGst) : '—'}</td>
                      </tr>
                    );
                  })}
                  {/* Totals row — only show on last page */}
                  {purchPage === purchTotalPages && (
                    <tr className="bg-slate-50 font-bold border-t-2 border-slate-200">
                      <td className="px-3 py-3 text-slate-900">TOTAL</td>
                      <td className="px-3 py-3 text-right text-slate-600">
                        {silicaPurchases.reduce((s, p) => s + (parseFloat(p.number_of_tons) || 0), 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-3 text-right text-slate-500">—</td>
                      <td className="px-3 py-3 text-right text-amber-600">
                        {formatCurrency(silicaPurchases.reduce((s, p) => s + (parseFloat(p.offline_amount) || 0), 0))}
                      </td>
                      <td className="px-3 py-3 text-right text-blue-600">
                        {formatCurrency(silicaPurchases.reduce((s, p) => s + (parseFloat(p.online_amount) || 0), 0))}
                      </td>
                      <td className="px-3 py-3 text-right text-slate-500">—</td>
                      <td className="px-3 py-3 text-right text-slate-900">
                        {formatCurrency(silicaPurchases.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={purchPage} totalPages={purchTotalPages} onPage={setPurchasePage} />
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>No silica purchases found</p>
            <p className="text-sm">Add expenses with "Silica Purchase" category to track inventory</p>
          </div>
        )}
      </Card>

      {/* ── 3. Recent Silica Loads ───────────────────────────────────────── */}
      <Card className="p-6 border-0 rounded-2xl shadow-lg">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-purple-600" />
            Silica Loads
          </h3>
          <span className="text-xs text-slate-400 mr-auto">{filteredLoads.length} of {silicaLoads.length} loads</span>

          <Select value={loadsClientFilter} onValueChange={(v) => { setLoadsClientFilter(v); setLoadsPage(1); }}>
            <SelectTrigger className="h-8 w-[140px] rounded-lg text-xs" data-testid="loads-client-filter">
              <SelectValue placeholder="Client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {loadsClientOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={loadsStatusFilter} onValueChange={(v) => { setLoadsStatusFilter(v); setLoadsPage(1); }}>
            <SelectTrigger className="h-8 w-[130px] rounded-lg text-xs" data-testid="loads-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>

          {hasLoadsFilters && (
            <Button
              data-testid="clear-loads-filters-btn"
              variant="outline"
              size="sm"
              onClick={() => { setLoadsClientFilter('all'); setLoadsStatusFilter('all'); setLoadsPage(1); }}
              className="h-8 w-8 p-0 rounded-lg border-red-300 text-red-600 hover:bg-red-50"
              title="Clear Filters"
            >
              <FilterX className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>

        {filteredLoads.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 uppercase">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 uppercase">Client</th>
                    <th className="px-3 py-3 text-left text-xs font-bold text-slate-500 uppercase">Vehicle</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Tons</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-slate-500 uppercase">Invoice Amt</th>
                    <th className="px-3 py-3 text-center text-xs font-bold text-slate-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadsRows.map((load, idx) => (
                    <tr key={load.load_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-3 text-slate-900">{new Date(load.date).toLocaleDateString('en-IN')}</td>
                      <td className="px-3 py-3 text-slate-600">{load.client_name}</td>
                      <td className="px-3 py-3 text-slate-600">{load.vehicle_number}</td>
                      <td className="px-3 py-3 text-right font-medium text-purple-600">
                        {(() => {
                          let tons = parseFloat(load.total_tons) || parseFloat(load.tons) || 0;
                          if (tons === 0 && load.kgs) tons = parseFloat(load.kgs) / 1000;
                          return tons.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                        })()}
                      </td>
                      <td className="px-3 py-3 text-right text-slate-600">{formatCurrency(load.invoice_amount || 0)}</td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          load.acceptance_status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                          load.acceptance_status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {load.acceptance_status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={loadsPageClamped} totalPages={loadsTotalPages} onPage={setLoadsPage} />
          </>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <TrendingDown className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{hasLoadsFilters ? 'No loads match the selected filters' : 'No silica loads found'}</p>
          </div>
        )}
      </Card>

      {/* FIFO Info */}
      <Card className="p-4 border-0 rounded-xl bg-blue-50">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">FIFO Calculation Method</p>
            <p>Inventory is calculated using First-In-First-Out (FIFO) method. Silica loads are deducted from the oldest purchase first. <strong>Remaining Value (GST)</strong> reflects the asset value at GST-inclusive prices.</p>
          </div>
        </div>
      </Card>
    </div>
  );
}
